// Mercapoli - Payment Success Page
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, XCircle, Package, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { payments } from '../../lib/api';
import { useCart } from '../../context/CartContext';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  
  const sessionId = searchParams.get('session_id');
  const pedidoId = searchParams.get('pedido_id');
  
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [paymentInfo, setPaymentInfo] = useState(null);

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else {
      setStatus('failed');
    }
  }, [sessionId]);

  const verifyPayment = async () => {
    try {
      const result = await payments.getPaymentStatus(sessionId);
      
      if (result.payment_status === 'paid') {
        setStatus('success');
        setPaymentInfo(result);
        clearCart(); // Clear cart after successful payment
      } else {
        setStatus('failed');
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      setStatus('failed');
    }
  };

  const goToOrderTracking = () => {
    navigate(`/app/pedido/${pedidoId || paymentInfo?.pedido_id}`);
  };

  const goToHome = () => {
    navigate('/app');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md text-center"
      >
        {status === 'loading' && (
          <>
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="h-10 w-10 text-primary animate-spin" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Verificando pago...</h1>
            <p className="text-muted-foreground">Por favor espera mientras confirmamos tu pago.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="h-10 w-10 text-green-600" />
            </motion.div>
            
            <h1 className="text-2xl font-bold mb-2">¡Pago exitoso!</h1>
            <p className="text-muted-foreground mb-6">
              Tu pago ha sido procesado correctamente y tu pedido está siendo preparado.
            </p>

            {paymentInfo && (
              <div className="bg-card rounded-xl border border-border p-4 mb-6 text-left">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-muted-foreground">Monto pagado</span>
                  <span className="font-bold text-lg">${paymentInfo.amount?.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Número de pedido</span>
                  <span className="font-mono">#{(pedidoId || paymentInfo.pedido_id)?.slice(-6).toUpperCase()}</span>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Button onClick={goToOrderTracking} className="w-full" size="lg">
                <Package className="h-5 w-5 mr-2" />
                Ver mi pedido
              </Button>
              <Button onClick={goToHome} variant="outline" className="w-full">
                Volver al inicio
              </Button>
            </div>

            <p className="text-sm text-muted-foreground mt-6">
              Recibirás un correo de confirmación con los detalles de tu pedido.
            </p>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-10 w-10 text-red-600" />
            </div>
            
            <h1 className="text-2xl font-bold mb-2">Error en el pago</h1>
            <p className="text-muted-foreground mb-6">
              No pudimos verificar tu pago. Si el cargo fue realizado, tu pedido será procesado automáticamente.
            </p>

            <div className="space-y-3">
              <Button onClick={() => navigate('/app/checkout')} className="w-full" size="lg">
                Intentar de nuevo
              </Button>
              <Button onClick={goToHome} variant="outline" className="w-full">
                Volver al inicio
              </Button>
            </div>

            <p className="text-sm text-muted-foreground mt-6">
              Si tienes problemas, contacta a soporte: soporte@mercapoli.com
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
